import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { merchantApi } from '../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell, faBook, faCheckCircle, faGear, faSave, faCopy, faFlask,
  faRotate, faLock, faList, faTimesCircle, faExclamationTriangle, faCode,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../components/shared/DataTable';
import { UiPageHeader, UiButton, UiInput, StatusBadge } from '../components/ui';
import type { StatusDef } from '../components/ui';

const LOG_STATUS_MAP: Record<string, StatusDef> = {
  success: { bg: 'bg-green-100', text: 'text-green-700', icon: faCheckCircle, label: 'Success' },
  failed:  { bg: 'bg-red-100', text: 'text-red-600', icon: faTimesCircle, label: 'Failed' },
};

const WebhookSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<any>({ webhook_url: '', has_secret: false });
  const [webhookUrl, setWebhookUrl] = useState('');
  const [logs, setLogs] = useState<any[]>([]);
  const [testing, setTesting] = useState(false);
  const [newSecret, setNewSecret] = useState('');

  const loadSettings = useCallback(async () => {
    try {
      const response = await merchantApi.get('/merchants/webhook/settings');
      setSettings(response.data.data);
      setWebhookUrl(response.data.data.webhook_url || '');
    } catch (error: any) {
      toast.error(t('common.error'));
    }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      const response = await merchantApi.get('/merchants/webhook/logs');
      setLogs(response.data.data || []);
    } catch (error: any) {
      console.error('Error loading logs');
    }
  }, []);

  useEffect(() => {
    loadSettings();
    loadLogs();
  }, [loadSettings, loadLogs]);

  const handleSave = async () => {
    try {
      await merchantApi.put('/merchants/webhook/settings', { webhook_url: webhookUrl });
      toast.success(t('webhookSettings.saveSuccess'));
      loadSettings();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('common.error'));
    }
  };

  const handleRegenerateSecret = async () => {
    if (!window.confirm(t('webhookSettings.regenerateConfirm'))) return;

    try {
      const response = await merchantApi.put('/merchants/webhook/settings', { regenerate_secret: true });
      setNewSecret(response.data.data.webhook_secret);
      toast.success(t('webhookSettings.regenerateSuccess'));
      loadSettings();
    } catch (error: any) {
      toast.error(t('common.error'));
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const response = await merchantApi.post('/merchants/webhook/test');
      if (response.data.success) {
        toast.success(t('webhookSettings.testSuccess'));
      } else {
        toast.error(t('webhookSettings.testFailed') + response.data.data.error);
      }
    } catch (error: any) {
      toast.error(t('common.error'));
    } finally {
      setTesting(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('common.copied'));
    } catch (err) {
      toast.error(t('common.cannotCopy'));
    }
  };

  const logColumns: Column<any>[] = [
    {
      key: 'order',
      header: 'Order',
      render: (log) => <span className="font-medium text-gray-800 dark:text-slate-100">{log.orders?.order_number || '-'}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (log) => <StatusBadge status={log.success ? 'success' : 'failed'} statusMap={LOG_STATUS_MAP} />,
    },
    {
      key: 'response',
      header: 'Response',
      render: (log) => <span className="text-sm text-gray-600 dark:text-slate-300">{log.response_status || '-'}</span>,
    },
    {
      key: 'retry',
      header: 'Retry',
      render: (log) => <span className="text-sm text-gray-600 dark:text-slate-300">{log.retry_count}</span>,
    },
    {
      key: 'created_at',
      header: 'วันที่',
      render: (log) => <span className="text-sm text-gray-500 dark:text-slate-400">{new Date(log.created_at).toLocaleString('th-TH')}</span>,
    },
  ];

  return (
    <div className="w-full">
      <UiPageHeader icon={faBell} title={t('webhookSettings.title')} subtitle={t('webhookSettings.desc')} />

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 mb-6 flex items-start gap-3">
        <FontAwesomeIcon icon={faBook} className="text-blue-500 text-xl mt-0.5" />
        <div>
          <h3 className="font-bold text-blue-800 dark:text-blue-300 mb-1">{t('webhookSettings.whatIsWebhook')}</h3>
          <p className="text-blue-700 dark:text-blue-400 text-sm">{t('webhookSettings.webhookExplain')}</p>
        </div>
      </div>

      {/* New Secret Alert */}
      {newSecret && (
        <div className="bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800 rounded-xl p-6 mb-6">
          <h3 className="text-green-800 dark:text-green-300 font-bold text-lg flex items-center gap-2 mb-3">
            <FontAwesomeIcon icon={faCheckCircle} />
            {t('webhookSettings.newSecret')}
          </h3>
          <div className="bg-white dark:bg-slate-800 rounded-lg p-4 flex items-center justify-between gap-3 mb-3">
            <code className="text-green-700 dark:text-green-400 font-mono break-all">{newSecret}</code>
            <button
              onClick={() => copyToClipboard(newSecret)}
              className="px-3 py-2 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 text-green-700 dark:text-green-400 rounded-lg transition-colors flex-shrink-0"
            >
              <FontAwesomeIcon icon={faCopy} />
            </button>
          </div>
          <p className="text-red-600 dark:text-red-400 text-sm">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
            {t('webhookSettings.onlyTimeSecret')}
          </p>
        </div>
      )}

      {/* Settings Card */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 mb-4 flex items-center gap-2">
          <FontAwesomeIcon icon={faGear} style={{ color: 'var(--theme-primary)' }} />
          Webhook Configuration
        </h2>

        <div className="mb-5">
          <UiInput
            label="Webhook URL"
            type="url"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://your-domain.com/webhook/payment"
            hint={t('webhookSettings.urlDescription')}
          />
        </div>

        <div className="flex flex-wrap gap-3 mb-6">
          <UiButton icon={faSave} onClick={handleSave}>
            {t('webhookSettings.saveUrl')}
          </UiButton>
          <UiButton variant="secondary" icon={faFlask} onClick={handleTest} disabled={!webhookUrl || testing}>
            {testing ? t('webhookSettings.testing') : t('webhookSettings.testWebhook')}
          </UiButton>
        </div>

        {/* Security Section */}
        <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-5 border border-gray-200 dark:border-slate-700">
          <h3 className="font-bold text-gray-800 dark:text-slate-100 mb-2 flex items-center gap-2">
            <FontAwesomeIcon icon={faLock} className="text-amber-500" />
            Webhook Security
          </h3>
          <p className="text-sm text-gray-600 dark:text-slate-300 mb-4">{t('webhookSettings.secretDesc')}</p>

          <div className="flex items-center gap-3 mb-4">
            {settings.has_secret ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon={faCheckCircle} /> {t('webhookSettings.hasSecret')}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-400 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon={faExclamationTriangle} /> {t('webhookSettings.noSecret')}
              </span>
            )}
          </div>

          <UiButton variant="danger" icon={faRotate} onClick={handleRegenerateSecret}>
            {t('webhookSettings.regenerateSecret')}
          </UiButton>
        </div>
      </div>

      {/* Documentation */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 mb-4 flex items-center gap-2">
          <FontAwesomeIcon icon={faBook} className="text-purple-500" />
          Webhook Payload
        </h2>
        <p className="text-gray-600 dark:text-slate-300 text-sm mb-4">{t('webhookSettings.payloadDesc')}</p>

        <div className="bg-gray-900 dark:bg-slate-950 rounded-xl p-5 mb-4 overflow-x-auto">
          <div className="flex items-center gap-2 text-gray-400 dark:text-slate-500 text-xs mb-2">
            <FontAwesomeIcon icon={faCode} />
            <span>JSON Payload</span>
          </div>
          <pre className="text-sm text-green-400 font-mono">{`{
  "event": "order.paid",
  "order_number": "ORD1728374929123",
  "status": "paid",
  "base_amount": 100.00,
  "total_amount": 100.45,
  "customer_name": "นาย A",
  "reference_id": "YOUR-REF-123",
  "paid_at": "2025-10-09T12:35:29.000Z"
}`}</pre>
        </div>

        <h3 className="font-semibold text-gray-700 dark:text-slate-200 mb-2">Headers:</h3>
        <div className="bg-gray-900 dark:bg-slate-950 rounded-xl p-4 mb-4 overflow-x-auto">
          <pre className="text-sm text-blue-400 font-mono">{`Content-Type: application/json
X-Webhook-Signature: <HMAC-SHA256 signature>
User-Agent: PaymentGateway-Webhook/1.0`}</pre>
        </div>

        <h3 className="font-semibold text-gray-700 dark:text-slate-200 mb-2">{t('webhookSettings.verifySignature')}</h3>
        <div className="bg-gray-900 dark:bg-slate-950 rounded-xl p-4 overflow-x-auto">
          <pre className="text-sm text-amber-400 font-mono">{`// Node.js Example
const crypto = require('crypto');

function verifyWebhook(payload, signature, secret) {
  const hmac = crypto
    .createHmac('sha256', secret)
    .update(JSON.stringify(payload))
    .digest('hex');
  return hmac === signature;
}`}</pre>
        </div>
      </div>

      {/* Logs */}
      {logs.length > 0 && (
        <DataTable<any>
          data={logs}
          columns={logColumns}
          keyExtractor={(log) => log.id}
          pagination={{ enabled: true, pageSize: 10, pageSizeOptions: [10, 20, 50], showPageSizeSelector: true, showInfo: true, showFirstLast: false }}
          headerContent={
            <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2">
              <FontAwesomeIcon icon={faList} className="text-indigo-500" />
              Webhook Logs
            </h2>
          }
          hoverable
        />
      )}
    </div>
  );
};

export default WebhookSettingsPage;
